import type { EnrollmentStartResponse } from '@/api/enrollment/start'
import type { EnrollmentFormData } from '@/stores'
import { normalizeSelfEnrollmentEntryStep } from '../../selfEnrollmentFlow/utils/steps'
import type { AddressParts, ApplicationFormState } from '../types'

type RegisterAddressParts = {
  city: string
  state: string
  zipCode: string
}

type PersistRegistrationResultArgs = {
  addressParts: RegisterAddressParts
  data: EnrollmentStartResponse
  setCurrentStep: (step: number) => void
  setSessionToken: (token: string | null) => void
  state: ApplicationFormState
  updateFormData: (data: Partial<EnrollmentFormData>) => void
}

type RegisterPayloadArgs = {
  addressParts: RegisterAddressParts
  state: ApplicationFormState
}

export function buildApplicationRegisterPayload({
  addressParts,
  state
}: RegisterPayloadArgs) {
  return {
    firstName: state.formData.firstName,
    lastName: state.formData.lastName,
    middleName: state.formData.middleName,
    email: state.formData.email,
    phoneNumber: state.formData.phoneNumber,
    addressLine1: state.formData.addressLine1,
    city: addressParts.city || undefined,
    state: addressParts.state || undefined,
    zipCode: addressParts.zipCode || undefined,
    about: state.formData.about,
    enrollmentPath: 'SELF_ENROLL' as const,
    marketingConsent: state.formData.termsAccepted,
    smsConsent: state.formData.termsAccepted
  }
}

export function persistRegistrationResult({
  addressParts,
  data,
  setCurrentStep,
  setSessionToken,
  state,
  updateFormData
}: PersistRegistrationResultArgs) {
  if (data.sessionToken) {
    setSessionToken(data.sessionToken)
  }

  if (typeof data.currentStep === 'number') {
    setCurrentStep(normalizeSelfEnrollmentEntryStep(data.currentStep))
  }

  updateFormData({
    firstName: data.firstName ?? '',
    lastName: data.lastName ?? '',
    middleName: state.formData.middleName,
    email: data.email ?? '',
    phone: data.phoneNumber ?? '',
    phoneNumber: data.phoneNumber ?? '',
    addressLine1: data.addressLine1 ?? state.formData.addressLine1,
    city: addressParts.city,
    state: addressParts.state,
    zipCode: addressParts.zipCode,
    birthDate: state.formData.birthDate,
    about: state.formData.about,
    termsAccepted: state.formData.termsAccepted,
    marketingConsent: state.formData.termsAccepted,
    smsConsent: state.formData.termsAccepted,
    contactExternalId: data.contactExternalId ?? '',
    applicationReadyAt: Date.now()
  })
}

export function toPersistedAddressParts(
  addressLine1: string,
  addressParts: RegisterAddressParts
): AddressParts {
  return {
    addressLine1,
    city: addressParts.city,
    state: addressParts.state,
    zipCode: addressParts.zipCode
  }
}
